import { useState, useEffect } from "react";
import { Grid, IconButton, ImageList, ImageListItem, ImageListItemBar } from "@mui/material";
import { Modal } from "@mui/material";
import { styled } from "@mui/material/styles";
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';

const ImageLayout = ({ images }) => {
    const [imageIndex, setImageIndex] = useState(0);
    const [viewImage, setViewImage] = useState(false);
    const ImageContainer = styled('div')(() => ({
        display: 'flex',
        alignItems: 'center',
        gap: '15px',
        outline: 'none',
    }));

    const handleImageClick = (index) => {
        setImageIndex(index);
        setViewImage(true);
    }

    const handleKeyDown = (event) => {
        if (!viewImage) {
            return;
        }
        if (event.key === 'ArrowLeft' && imageIndex > 0) {
            setImageIndex(imageIndex - 1);
        } else if (event.key === 'ArrowRight' && imageIndex < images.length - 1) {
            setImageIndex(imageIndex + 1);
        } else if (event.key === 'Escape') {
            setViewImage(false);
        }
    }

    useEffect(() => {
        window.addEventListener('keydown', handleKeyDown);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    });

    const ImageItem = ({ url, caption, index }) => {
        return (
            <ImageListItem
                sx={{
                    '&:hover .MuiImageListItemBar-root': {
                        visibility: 'visible',
                    },
                    cursor: 'pointer',
                }}
                onClick={viewImage ? undefined : () => handleImageClick(index)}
            >
                <img
                    style={{
                        padding: '2px',
                        borderRadius: '5px',
                        backgroundColor: '#f0f0f0',
                        maxWidth: '80vw',
                        maxHeight: '80vh',
                    }}
                    src={`data:image/png;base64,${url}`}
                    alt={caption}
                    loading="lazy"
                />
                <ImageListItemBar
                    sx={{
                        '& *': {
                            padding: '3px',
                        },
                        opacity: '0.7',
                        visibility: 'hidden',
                    }}
                    title={caption}
                />
            </ImageListItem>
        );
    };

    const ImagePopup = () => {
        return (
            <Modal
                open={viewImage}
                onClose={() => setViewImage(false)}
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',

                }}
            >
                <ImageContainer>
                    <IconButton
                        disabled={imageIndex === 0}
                        onClick={() => setImageIndex(imageIndex - 1)}
                        sx={{
                            color: 'white',
                        }}
                    >
                        <ArrowBackIosIcon />
                    </ IconButton>
                    <ImageItem url={images[imageIndex]?.url} caption={images[imageIndex]?.caption} index={imageIndex} />
                    <IconButton
                        disabled={imageIndex === images.length - 1}
                        onClick={() => setImageIndex(imageIndex + 1)}
                        sx={{
                            color: 'white',
                        }}
                    >
                        <ArrowForwardIosIcon />
                    </IconButton>
                </ImageContainer>
            </Modal>
        );
    }

    const ImageCarousel = ({ images, size = 5 }) => {
        const [imageRange, setImageRange] = useState({
            start: 0,
            end: Math.min(size, images.length),
        });

        return (
            <Grid
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                }}
            >
                <IconButton
                    onClick={() => setImageRange({
                        start: imageRange.start - 1,
                        end: imageRange.end - 1,
                    })}
                    sx={{
                        display: imageRange.start > 0 ? 'block' : 'none',
                        padding: '2px 10px',
                        backgroundColor: '#f0f0f0',
                    }}
                >
                    <ArrowBackIosIcon color="primary" fontSize="small" />
                </ IconButton>
                <ImageList cols={size} >
                    {images?.slice(imageRange.start, imageRange.end)?.map((img, index) => (
                        <ImageItem key={index} url={img?.url} caption={img?.caption} index={index} />
                    ))}
                </ImageList>
                <IconButton
                    onClick={() => setImageRange({
                        start: imageRange.start + 1,
                        end: imageRange.end + 1,
                    })}
                    sx={{
                        display: images.length > imageRange.end + 1 ? 'block' : 'none',
                        padding: '2px 10px',
                        backgroundColor: '#f0f0f0',
                    }}
                >
                    <ArrowForwardIosIcon color="primary" fontSize="small" />
                </IconButton>
            </Grid>
        );
    }

    return (
        <>
            <ImagePopup />
            <ImageCarousel images={images} size={5} />
        </>
    );
}

export default ImageLayout;